import { FC, useState } from 'react';

import Select from 'epn-ui/umd/dist/components/Select';
import Input from 'epn-ui/umd/dist/components/InputText';

import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faSlidersV } from '@fortawesome/pro-regular-svg-icons/faSlidersV';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { isGlobalDomain } from '@config/globalDomain';

import { IGetPlacementsDataFormatted } from '@api/types';
import BaseEmpty from '@base/BaseEmpty';
import BaseSanitizer from '@base/BaseSanitizer';
import useI18n from '@i18n';
import ExportButton from '@pages/offers/ExportButton';
import FilterButton from '@pages/offers/FilterButton';

const MIN_SEARCH_LENGTH = 2;

interface IOffersHeaderProps {
    offersCount: number;
    onChange: (fieldName: string, value: string | number) => void;
    isNotFetchingAndEmpty: boolean;
    onExportOffers: (exportFormat: string) => void;
    filtersIsVisible: boolean;
    toggleFilters: () => void;
    uniqPlacementOptions: { value: string; label: string }[];
    values: { order: string; placement: IGetPlacementsDataFormatted; search: string };
    isAuth: boolean;
    onSearch: (value: string) => void;
}
export interface IUniqPlacementOption {
    label: string;
    value: string;
    info: IGetPlacementsDataFormatted;
}

export const OffersHeader: FC<IOffersHeaderProps> = ({
    offersCount,
    uniqPlacementOptions,
    onChange,
    isNotFetchingAndEmpty = false,
    onExportOffers,
    filtersIsVisible,
    toggleFilters,
    values,
    isAuth,
    onSearch,
}) => {
    const { t } = useI18n('Offers');
    const orderOptions = [
        {
            label: t('By popularity'),
            value: 'webmasterPriority',
        },
        {
            label: t('By launch date'),
            value: 'new',
        },
    ];
    const getValue = (
        selectOptions: {
            label: string;
            value: string | number;
        }[],
        value: string | null,
    ) => selectOptions?.find(option => option.value === value) || null;

    const [search, setSearch] = useState(values?.search);

    const onChangePlacement = value => {
        onChange('placement', value);
        localStorage.setItem('offersPlacementId', JSON.stringify(value));
    };

    const onSearchChange = value => {
        const newValLength = value.length;
        const oldValLength = search?.length;

        setSearch(value);

        if (newValLength >= MIN_SEARCH_LENGTH || newValLength < oldValLength)
            return onSearch(value);

        return value;
    };

    return (
        <div className="flex flex-col flex-wrap items-start justify-start mb-2 md:justify-between md:flex-row">
            <div className="flex flex-col gap-[8px]">
                <div className="flex items-center justify-start gap-[16px]">
                    <p className="font-medium text-subtitle xs:text-h2 text-blueGray-800">
                        {t('Offers catalog')}
                    </p>
                    {!!offersCount && (
                        <p className="font-medium bg-blueGray-100 rounded-[6px] py-[2px] px-[8px] text-[14px] text-blueGray-500 leading-[21px]">
                            {offersCount}
                        </p>
                    )}
                </div>
                {!isGlobalDomain && (
                    <div className="flex mb-6 text-blueGray-800 text-p2">
                        <BaseSanitizer
                            html={t('How to work with the offer catalog')}
                            tag="p"
                            options={{ allowedTags: ['a'] }}
                        />
                        {'\u00A0'}
                        <a
                            href="https://epn.bz/ru/info/faq/izmeneniya-na-stranice-offerov/"
                            className="underline"
                        >
                            {`${t('see here')}`}
                        </a>
                    </div>
                )}
            </div>
            <div className="flex flex-col justify-end w-full xs:flex-row gap-[8px] xs:gap-[16px] md:w-auto xs:flex-wrap md:flex-nowrap">
                {isAuth && (
                    <Select
                        id="PlacementSelect"
                        className="w-full min-w-[150px] md:min-w-[180px] lg:min-w-[200px] xl:min-w-[250px] md:max-w-[304px] xs:w-[calc(50%-8px)]"
                        value={getValue(uniqPlacementOptions, values?.placement?.id)}
                        onChange={onChangePlacement}
                        options={uniqPlacementOptions}
                        placeholder={t('Select placement')}
                        notFoundContent={<BaseEmpty />}
                    />
                )}
                <div className="flex justify-end w-full gap-[16px] xs:w-[calc(50%-8px)] md:w-auto">
                    <Select
                        id="OffersOrderSelect"
                        className="w-full"
                        value={getValue(orderOptions, values?.order)}
                        onChange={value => onChange('order', value)}
                        options={orderOptions}
                        placeholder={t('Sort type')}
                        notFoundContent={<BaseEmpty />}
                    />
                    {isAuth && (
                        <ExportButton
                            disabled={isNotFetchingAndEmpty}
                            onExportOffers={onExportOffers}
                        />
                    )}
                </div>
                <Input
                    placeholder={t('Search')}
                    prefix={<FontAwesomeIcon className="text-blueGray-500" icon={faSearch} />}
                    onChange={e => onSearchChange(e.target.value)}
                    className="md:hidden"
                />
            </div>
            <div className="w-full md:hidden my-[24px]">
                <FilterButton
                    isOpen={filtersIsVisible}
                    onClick={toggleFilters}
                    icon={filtersIsVisible ? faTimes : faSlidersV}
                    filtersDirty={false}
                />
            </div>
        </div>
    );
};
